import { useState, useCallback, useMemo, useRef } from 'react';
import type { Step, EventData } from 'react-joyride';
import { useStore } from '@store/GlobalStore';
import { useUpdateUser } from '@services/user/useUpdateUser';
import { shallow } from 'zustand/shallow';
import type { UserDisplayPreferences } from '@services/userRoles';

const WALKTHROUGH_KEY = 'requireBeforeBind' as const;
const TARGET_SELECTOR = '[data-panel-status="bindIncomplete"]';
const DAGGER_SELECTOR = '[data-panel-status="bindIncomplete"] [data-required-before-bind]';
const FIRST_STEP_DEFAULT_PLACEMENT: Step['placement'] = 'top';
const FIRST_LAST_STEP_FIRST_PANEL_PLACEMENT: Step['placement'] = 'left-start';
// Only show walkthrough to users whose accounts existed before the feature launched
const WALKTHROUGH_CUTOFF_DATE = new Date('2026-03-30');

const INITIAL_POLL_DELAY_MS = 200;
const TARGET_POLL_INTERVAL_MS = 100;
const TARGET_POLL_MAX_ATTEMPTS = 100;
const SCROLL_CORRECTION_DELAY_MS = 250;
const FORCE_LOAD_STEP_DELAY_MS = 20;
const FORCE_LOAD_SETTLE_DELAY_MS = 200;
const DAGGER_RETRY_DELAY_MS = 150;
const DAGGER_MAX_RETRIES = 8;

function getWalkthroughSteps(firstLastStepPlacement: Step['placement']): Step[] {
  return [
    {
      target: TARGET_SELECTOR,
      title: "What's New: Required Before Bind",
      content:
        "You'll notice some panels now have a yellow highlight. This means there are fields in this section that do not need to be completed for rating, but must be completed before the quote can be bound.",
      placement: firstLastStepPlacement,
      skipBeacon: true,
      skipScroll: true,
    },
    {
      target: '[data-panel-status="bindIncomplete"] [data-testid="panel-status-icon"]',
      title: 'The Yellow Circle Indicator',
      content:
        'This yellow circle in the panel header and Quote Navigator sidebar indicates the panel has Required Before Bind fields that still need to be completed.',
      placement: 'bottom',
      skipBeacon: true,
      skipScroll: true,
    },
    {
      target: DAGGER_SELECTOR,
      title: 'The † Symbol',
      content:
        'Individual fields marked with a † symbol are the Required Before Bind fields. These must be completed before the quote can be bound, but are not required for rating.',
      placement: 'bottom',
      skipBeacon: true,
      skipScroll: true,
    },
    {
      target: TARGET_SELECTOR,
      title: 'Summary',
      content:
        'You can still rate the quote and generate quote documents if all panels are green and yellow. However, all panels must be green before the quote can be bound. Edit any yellow panels before bind to complete the missing information.',
      placement: firstLastStepPlacement,
      skipBeacon: true,
      skipScroll: true,
    },
  ];
}

function isFirstPanel(target: HTMLElement): boolean {
  const targetPanel = target.closest<HTMLElement>('[data-panel-status]');
  const firstPanel = document.querySelector<HTMLElement>('[data-panel-status]');
  return !!targetPanel && targetPanel === firstPanel;
}

/**
 * Walk up from the target to find the first ancestor that actually scrolls
 * (scrollHeight > clientHeight).
 */
function findScrollableAncestor(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;
  while (parent && parent !== document.documentElement) {
    if (parent.scrollHeight > parent.clientHeight + 1) {
      const style = getComputedStyle(parent);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return parent;
      }
    }
    parent = parent.parentElement;
  }
  return document.documentElement;
}

/**
 * Scroll the actual scrollable container so the target is near the top.
 * Re-checks after each scroll in case lazy content shifts layout.
 */
function scrollTargetIntoView(target: HTMLElement): Promise<boolean> {
  const container = findScrollableAncestor(target);
  if (!container) return Promise.resolve(false);

  const padding = 250; // leave room above the panel for the Joyride tooltip
  let corrections = 0;
  const maxCorrections = 5;

  return new Promise((resolve) => {
    const correct = () => {
      corrections++;

      const containerTop = container.getBoundingClientRect().top;
      const targetTop = target.getBoundingClientRect().top;
      const currentOffset = targetTop - containerTop;

      if (Math.abs(currentOffset - padding) < 15) {
        resolve(true);
        return;
      }

      if (corrections > maxCorrections) {
        resolve(targetTop >= containerTop && targetTop < containerTop + container.clientHeight);
        return;
      }

      container.scrollTop += currentOffset - padding;
      setTimeout(correct, SCROLL_CORRECTION_DELAY_MS);
    };

    correct();
  });
}

/**
 * Force all lazy-loaded sections inside the target panel to render by briefly
 * scrolling the page container through the panel's full height. This triggers
 * the IntersectionObservers (rootMargin: 5000px) that gate section rendering.
 * After triggering, scrolls back to the original position.
 */
function forceLoadPanelSections(panel: HTMLElement): Promise<void> {
  const container = findScrollableAncestor(panel);
  if (!container) return Promise.resolve();

  const panelRect = panel.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const panelTopInContainer = container.scrollTop + (panelRect.top - containerRect.top);
  const panelBottomInContainer = panelTopInContainer + panel.scrollHeight;
  const savedScrollTop = container.scrollTop;

  const chunkSize = container.clientHeight;
  let scrollPos = panelTopInContainer;

  return new Promise<void>((resolve) => {
    const step = () => {
      if (scrollPos >= panelBottomInContainer) {
        container.scrollTop = savedScrollTop;
        // Give observers time to fire and React to render
        setTimeout(() => resolve(), FORCE_LOAD_SETTLE_DELAY_MS);
        return;
      }
      container.scrollTop = scrollPos;
      scrollPos += chunkSize;
      setTimeout(step, FORCE_LOAD_STEP_DELAY_MS);
    };
    step();
  });
}

export const useRequireBeforeBindWalkthrough = (hasMissingRequiredBeforeBind: boolean) => {
  const [run, setRun] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [firstStepPlacement, setFirstStepPlacement] = useState<Step['placement']>(FIRST_STEP_DEFAULT_PLACEMENT);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [currentUser, walkthroughs] = useStore((state) => [state.currentUser, state.walkthroughs], shallow);
  const { updateUser } = useUpdateUser({ isEditingOwnProfile: true });

  const hasSeenWalkthrough = walkthroughs[WALKTHROUGH_KEY] === true;

  const isExistingUser = useMemo(() => {
    if (!currentUser?.createdAt) return false;
    return new Date(currentUser.createdAt) < WALKTHROUGH_CUTOFF_DATE;
  }, [currentUser?.createdAt]);

  const shouldShowWalkthrough = useMemo(
    () => hasMissingRequiredBeforeBind && !hasSeenWalkthrough && !hasStarted && isExistingUser,
    [hasMissingRequiredBeforeBind, hasSeenWalkthrough, hasStarted, isExistingUser],
  );
  const steps = useMemo(() => getWalkthroughSteps(firstStepPlacement), [firstStepPlacement]);

  const startWalkthrough = useCallback(() => {
    if (!shouldShowWalkthrough) return;
    setHasStarted(true);

    let attempts = 0;
    const maxAttempts = TARGET_POLL_MAX_ATTEMPTS;

    const pollForTarget = () => {
      attempts++;
      const target = document.querySelector<HTMLElement>(TARGET_SELECTOR);

      if (target) {
        setFirstStepPlacement(
          isFirstPanel(target) ? FIRST_LAST_STEP_FIRST_PANEL_PLACEMENT : FIRST_STEP_DEFAULT_PLACEMENT,
        );
        // Force all lazy sections in this panel to load so the † fields render
        forceLoadPanelSections(target).then(() => {
          // Now scroll the panel into proper position for the walkthrough
          scrollTargetIntoView(target).then((inView) => {
            if (inView) {
              // Wait for the † fields to render after sections loaded
              const waitForDagger = (retries: number) => {
                const dagger = document.querySelector(DAGGER_SELECTOR);
                if (dagger || retries <= 0) {
                  setRun(true);
                } else {
                  pollTimerRef.current = setTimeout(() => waitForDagger(retries - 1), DAGGER_RETRY_DELAY_MS);
                }
              };
              waitForDagger(DAGGER_MAX_RETRIES);
            } else if (attempts < maxAttempts) {
              pollTimerRef.current = setTimeout(pollForTarget, TARGET_POLL_INTERVAL_MS);
            }
          });
        });
      } else if (attempts < maxAttempts) {
        pollTimerRef.current = setTimeout(pollForTarget, TARGET_POLL_INTERVAL_MS);
      }
    };

    // Initial delay for page settle, then start polling
    pollTimerRef.current = setTimeout(pollForTarget, INITIAL_POLL_DELAY_MS);
  }, [shouldShowWalkthrough]);

  const markWalkthroughComplete = useCallback(async () => {
    if (!currentUser) return;

    const existingPrefs = (currentUser.displayPreferences as UserDisplayPreferences) ?? {};
    const updatedWalkthroughs = { ...existingPrefs.walkthroughs, [WALKTHROUGH_KEY]: true };

    try {
      await updateUser({
        requestBody: {
          displayPreferences: {
            ...existingPrefs,
            walkthroughs: updatedWalkthroughs,
          } as Record<string, unknown>,
        },
        requestParams: { userId: currentUser.id },
      });
    } catch (error) {
      console.warn('Failed to save walkthrough preference:', error);
    }
  }, [currentUser, updateUser]);

  const handleJoyrideEvent = useCallback(
    (data: EventData) => {
      const finishedStatuses: string[] = ['finished', 'skipped'];
      if (finishedStatuses.includes(data.status)) {
        setRun(false);
        markWalkthroughComplete();
      }

      // Custom scroll: when advancing to a step, scroll its target into view
      // since we use skipScroll on all steps to prevent Joyride's default scroll behavior
      if (data.type === 'step:before' && data.step?.target) {
        const selector = typeof data.step.target === 'string' ? data.step.target : null;
        if (selector) {
          const el = document.querySelector<HTMLElement>(selector);
          if (el) {
            const container = findScrollableAncestor(el);
            if (container) {
              const containerTop = container.getBoundingClientRect().top;
              const elTop = el.getBoundingClientRect().top;
              const offset = elTop - containerTop;
              const padding = 250;
              if (offset < padding || offset > container.clientHeight - padding) {
                container.scrollTop += offset - padding;
              }
            }
          }
        }
      }
    },
    [markWalkthroughComplete],
  );

  return {
    steps,
    run,
    startWalkthrough,
    handleJoyrideEvent,
    shouldShowWalkthrough,
    hasSeenWalkthrough,
  };
};
